import { Controller } from "@hotwired/stimulus"

// In-page category toggling for the child-category thumb strip
// (collection/_child_nav.html.erb). A plain tile click visits the tile's own
// canonical href — child page, parent, or parent ?cats= union — inside the
// collection_list turbo frame (the same frame the filter panel drives), so
// navigation remains in the current document. The frame response ships the
// destination shell (visible chrome, filter drawer, head metadata, JSON-LD)
// as inert data that collection_controller applies on turbo:frame-load, and
// the URL advances, so page identity follows: back/forward restore the state
// belonging to each URL.
//
// The server computes every href (child_nav_toggle_path); modified clicks
// and crawlers follow them natively. This controller only keeps the tiles'
// selected states and toggle hrefs in sync between frame visits.
export default class extends Controller {
  static values = { parentPermalink: String }

  connect() {
    this.handleFrameLoad = this.#handleFrameLoad.bind(this)
    document.addEventListener("turbo:frame-load", this.handleFrameLoad)
    this.#syncFromUrl(new URL(window.location.href))
  }

  disconnect() {
    document.removeEventListener("turbo:frame-load", this.handleFrameLoad)
  }

  toggle(event) {
    // Modified clicks keep native behavior (new tab on the canonical href).
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    event.preventDefault()

    // The href already encodes "the current selection with this tile
    // flipped" — capture it before #syncTiles recomputes it.
    const url = event.currentTarget.getAttribute("href")

    // Do not mutate the strip before the visit: Turbo captures the origin page
    // snapshot synchronously here. The frame-load handler applies destination
    // state from the completed frame URL, keeping Back/Forward authoritative.
    Turbo.visit(url, { frame: "collection_list", action: "advance" })
  }

  #handleFrameLoad(event) {
    if (event.target.id !== "collection_list") return

    const destination = new URL(event.target.src || window.location.href, window.location.origin)
    this.#syncFromUrl(destination)
  }

  #syncFromUrl(url) {
    const links = this.#links()
    const normalizePath = (path) => path.replace(/\/+$/, "") || "/"
    const destinationPath = normalizePath(url.pathname)
    const parentPath = normalizePath(this.parentPermalinkValue)
    let selectedSlugs = new Set()

    if (destinationPath === parentPath) {
      selectedSlugs = new Set(
        (url.searchParams.get("cats") || "").split(",").filter(Boolean)
      )
    } else {
      const child = links.find((link) => normalizePath(link.dataset.permalink) === destinationPath)
      if (child) selectedSlugs.add(child.dataset.slug)
    }

    links.forEach((link) => {
      link.classList.toggle("ccn-selected", selectedSlugs.has(link.dataset.slug))
    })
    this.#syncTiles()
  }

  // Links in DOM order == strip order, so joined slugs match what
  // child_nav_toggle_path produces for the same selection.
  #links() {
    return Array.from(this.element.querySelectorAll(".ccn-link"))
  }

  // After a toggle every tile's href is stale — each encodes "the selection
  // with me flipped". Recompute them all against the new selection, mirroring
  // child_nav_toggle_path: none → parent, one → that child's page, several →
  // parent ?cats= union.
  #syncTiles() {
    const links = this.#links()
    const selected = new Set(
      links.filter((l) => l.classList.contains("ccn-selected")).map((l) => l.dataset.slug)
    )

    links.forEach((link) => {
      if (link.classList.contains("ccn-selected")) {
        link.setAttribute("aria-current", "true")
      } else {
        link.removeAttribute("aria-current")
      }

      const flipped = new Set(selected)
      if (flipped.has(link.dataset.slug)) {
        flipped.delete(link.dataset.slug)
      } else {
        flipped.add(link.dataset.slug)
      }

      const picked = links.filter((l) => flipped.has(l.dataset.slug))
      if (picked.length === 0) {
        link.href = this.parentPermalinkValue
      } else if (picked.length === 1) {
        link.href = picked[0].dataset.permalink
      } else {
        link.href = `${this.parentPermalinkValue}?cats=${picked.map((l) => l.dataset.slug).join(",")}`
      }
    })
  }
}
